import React from 'react';
import SEO from '../components/SEO';
import { StructuredData, breadcrumbSchema } from '../components/StructuredData';
import './Legal.css';

const Terms: React.FC = () => {
  return (
    <div className="legal-page">
      <SEO 
        title="Terms of Service - TableChat"
        description="TableChat Terms of Service. Review the terms governing use of our restaurant communication platform, NFC messaging system, and related services."
        canonical="https://tablechat.io/terms"
      />
      <StructuredData data={breadcrumbSchema([
        { name: 'Home', url: 'https://tablechat.io' },
        { name: 'Terms of Service', url: 'https://tablechat.io/terms' },
      ])} />
      <div className="container">
        <div className="legal-header">
          <h1>Terms of Service</h1>
          <p className="last-updated">Last updated: October 14, 2025</p>
        </div>

        <div className="legal-content">
          <section>
            <h2>1. Overview</h2>
            <p>
              These Terms of Service ("Terms") govern access to and use of TableChat's software and services,
              including the restaurant-facing applications (e.g., management/admin consoles and server applications),
              the patron web app, NFC-based table messaging features, related websites, and any hardware we supply or
              recommend (collectively, the "Services"). "TableChat," "we," "us," and "our" refer to the provider of
              the Services. "You" and "Customer" refer to the restaurant client entering into these Terms. Patrons who
              interact through the web app at restaurant tables do so without creating accounts; sections that refer to
              "Accounts" apply only to restaurant clients and their authorized staff.
            </p>
            <p>By using the Services, you agree to these Terms.</p>
          </section>

          <section>
            <h2>2. Accounts and Eligibility (Restaurant Clients Only)</h2>
            <p>
              <strong>2.1 Restaurant Accounts.</strong> Access to the management/admin console and server applications is
              available only to restaurant clients and their authorized staff. Account creation, role assignment, and
              staff onboarding are performed by or for the restaurant client. Patrons do not register accounts to use the
              patron web app.
            </p>
            <p>
              <strong>2.2 Authorized Users.</strong> You are responsible for verifying staff identities and managing their
              roles and permissions. You must ensure that only authorized personnel access your account and that login
              credentials are protected.
            </p>
            <p>
              <strong>2.3 Accurate Information.</strong> You must provide and maintain accurate business and billing
              information.
            </p>
          </section>

          <section>
            <h2>3. Patron Experience (Anonymous Use)</h2>
            <p>
              Patrons access TableChat by tapping an NFC tag and opening the web app link. No patron registration is
              required. Patrons may submit messages through the web app; message content is visible to the restaurant and
              is processed by the Services for delivery, moderation, analytics, and support. Restaurants are responsible
              for patron-facing disclosures at their premises as required by law.
            </p>
          </section>

          <section>
            <h2>4. Services and Fees</h2>
            <p>
              <strong>4.1 Subscription.</strong> The Services are provided on a subscription basis for restaurants. There
              is a recurring monthly subscription fee. We do not list numeric pricing here; current pricing, terms, and
              any promotional offers are provided separately in ordering documentation or your service order.
            </p>
            <p>
              <strong>4.2 Hardware.</strong> TableChat functionality typically requires NFC tags or similar hardware.
              Hardware entails a cost separate from the subscription. Pricing, minimum quantities, shipping, and
              replacement policies are set forth in your order documentation.
            </p>
            <p>
              <strong>4.3 Taxes and Charges.</strong> Fees are exclusive of taxes, duties, and other governmental charges,
              all of which are your responsibility.
            </p>
            <p>
              <strong>4.4 Billing and Payment.</strong> We use third-party payment processors (e.g., Stripe). You
              authorize us and our processor to charge your chosen payment method for recurring subscription fees and any
              one-time hardware or setup fees. Late, declined, or past-due amounts may result in suspension until payment
              is resolved.
            </p>
          </section>

          <section>
            <h2>5. Implementation and Use</h2>
            <p>
              <strong>5.1 NFC and Web App.</strong> NFC tags generally encode a URL that routes the patron to your
              restaurant's table session. Connectivity, device capabilities, and local network or carrier conditions can
              affect performance. You are responsible for on-premise placement and maintenance of tags and any signage.
            </p>
            <p>
              <strong>5.2 Restaurant Responsibilities.</strong> You control your restaurant configuration (e.g., tables,
              staff roles, response workflows). You are responsible for:
            </p>
            <ul>
              <li>Ensuring patron content and staff interactions comply with law and your policies.</li>
              <li>Maintaining accurate contact and billing information.</li>
              <li>Training staff on appropriate use and response times.</li>
              <li>Displaying any legally required patron notices at your premises.</li>
            </ul>
            <p>
              <strong>5.3 Acceptable Use.</strong> You and your staff must not misuse the Services (e.g., no illegal
              content, harassment, spamming, attempts to bypass security, or reverse engineering). We may suspend or
              terminate access for violations.
            </p>
            <p>
              As an interactive service provider, we facilitate communication between patrons and restaurants but do not
              create or control the content exchanged. Each user is responsible for their own statements and materials.
              Consistent with 47 U.S.C. § 230 and similar safe-harbor laws where applicable, we are not liable for
              user-generated content or messages transmitted through the platform.
            </p>
            <p>
              <strong>5.4 No Expectation of Privacy in TableChat.</strong> The TableChat feature is designed for
              communication between patrons, servers, and restaurant managers in a shared restaurant environment. Messages
              transmitted through the Service may be visible to all participants in the same table session and to
              authorized restaurant staff. Although transmissions are encrypted in transit and stored securely, users
              should have no expectation of confidentiality or privacy regarding message content once submitted. You agree
              not to transmit information that you consider sensitive or private through the table-chat feature.
              TableChat disclaims all liability for disclosures or access to message content by other participants or
              restaurant personnel. By using the table-chat feature, you acknowledge and agree that your communications
              may be viewed by other participants and the restaurant.
            </p>
          </section>

          <section>
            <h2>6. Service Availability; Support</h2>
            <p>
              <strong>6.1 Availability.</strong> We strive for reliable operation, but the Services may be unavailable
              due to maintenance, updates, network or third-party outages, or force majeure events. Some features depend
              on third-party platforms (e.g., Firebase, Stripe), which may experience incidents outside our control.
            </p>
            <p>
              <strong>6.2 Updates.</strong> We may update, improve, or modify features periodically. Material changes that
              reduce core functionality will be communicated where practical.
            </p>
            <p>
              <strong>6.3 Support.</strong> We provide reasonable technical support during standard business hours and may
              offer enhanced support per your order.
            </p>
          </section>

          <section>
            <h2>7. Data and Privacy</h2>
            <p>
              <strong>7.1 Privacy.</strong> Our Privacy Policy (available at the /privacy page on this site) describes
              how we collect, use, and safeguard information. For restaurant staff accounts, we act as a controller for
              certain business contact and billing data. For patron messages and session metadata, we generally act as a
              processor on behalf of the restaurant. Where required, a data processing agreement (DPA) is available upon
              request.
            </p>
            <p>
              <strong>7.2 Security.</strong> We employ industry-standard security measures (including Firebase platform
              controls, application-level safeguards, and anti-abuse protections). No method of transmission or storage is
              perfectly secure; you are responsible for on-premise safeguards (e.g., device physical security and network
              hygiene).
            </p>
          </section>

          <section>
            <h2>8. Intellectual Property and Feedback</h2>
            <p>
              <strong>8.1 Ownership of Services.</strong> We and our licensors retain all right, title, and interest in 
              and to the Services, software, websites, hardware designs, trademarks, trade names, logos, and other related 
              intellectual property, including all enhancements, updates, modifications, or derivative works thereof. You 
              receive a limited, non-exclusive, non-transferable license to access and use the Services during your 
              subscription term in accordance with these Terms. Except as expressly stated, no rights are granted to you 
              under any patents, copyrights, trade secrets, trademarks, or other intellectual-property rights. You may not 
              remove proprietary notices, copy, modify, or create derivative works of the Services except as permitted by law.
            </p>
            <p>
              <strong>8.2 Trademarks.</strong> TableChat™ is a trademark. All rights reserved.
              Unauthorized use of the TableChat name, logo, or any associated trademarks is strictly prohibited.
            </p>
            <p>
              <strong>8.3 Feedback; Suggestions; No Transfer of Rights.</strong> We welcome feedback and suggestions in 
              response to our requests for input (for example, surveys, beta programs, or support interactions). However, 
              any feedback, suggestions, ideas, improvements, enhancements, feature requests, or other input you provide to 
              us (collectively, "Feedback"), whether solicited or unsolicited, may be used by us, our affiliates, successors, 
              assigns, licensees, or other third parties for any purpose, without any obligation to you or your users.
            </p>
            <p>
              You hereby grant to us and our affiliates a perpetual, irrevocable, royalty-free, worldwide, fully paid-up, 
              non-exclusive license (with the right to sublicense) to use, reproduce, modify, adapt, publish, distribute, 
              and otherwise exploit such Feedback in any manner and in any medium now known or later developed.
            </p>
            <p>
              You acknowledge and agree that (a) you have no right, title, or interest, (b) you will not assert any claim 
              in or to any such Feedback or any derivative works or improvements arising from it, and (c) these Terms do 
              not create any expectation of compensation, credit, or ownership rights in us. We are under no obligation to 
              use, implement, or incorporate any Feedback, and if we do so it will be without obligation to you or any 
              third party.
            </p>
            <p>
              <strong>8.4 No Unsolicited Ideas.</strong> Except for Feedback that we explicitly request, we do not accept 
              unsolicited proposals for new products, services, marketing campaigns, or similar creative concepts. If you 
              nonetheless submit such unsolicited materials, you agree that they will be treated as "Feedback" under Section 
              8.3 above and may be used by us without any restriction or obligation.
            </p>
          </section>

          <section>
            <h2>9. Hardware; Risk of Loss</h2>
            <p>
              Hardware is sold or supplied "as is" unless a written hardware warranty is provided in your order. Risk of
              loss passes upon delivery. You are responsible for installation, proper use, and compliance with applicable
              laws and venue policies. Return, RMA, or replacement terms (if any) are stated in your order.
            </p>
          </section>

          <section>
            <h2>10. Third-Party Services</h2>
            <p>
              The Services integrate with third parties (e.g., Stripe for payments, Google/Firebase for hosting and
              messaging infrastructure, reCAPTCHA for abuse protection). Your use of those services may be subject to
              their terms and policies.
            </p>
          </section>

          <section>
            <h2>11. Disclaimers</h2>
            <p>
              THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE." TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM
              ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
              PURPOSE, AND NON-INFRINGEMENT. THE SERVICES ARE NOT FOR EMERGENCY COMMUNICATIONS. PERFORMANCE MAY VARY WITH
              NETWORK, DEVICE, OR THIRD-PARTY PLATFORM CONDITIONS.
            </p>
          </section>

          <section>
            <h2>12. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL,
              CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR LOST PROFITS, REVENUE, DATA, OR GOODWILL. OUR TOTAL
              LIABILITY FOR ANY CLAIMS RELATING TO THE SERVICES WILL NOT EXCEED THE AMOUNTS PAID BY YOU TO US FOR THE
              SERVICES IN THE 12 MONTHS BEFORE THE EVENT GIVING RISE TO LIABILITY.
            </p>
          </section>

          <section>
            <h2>13. Indemnification</h2>
            <p>
              You will indemnify and hold us harmless from claims arising out of (a) your use of the Services in
              violation of these Terms or law; (b) your content or data; (c) disputes with patrons or staff; and (d)
              your hardware installation, placement, or maintenance.
            </p>
          </section>

          <section>
            <h2>14. Term; Suspension; Termination</h2>
            <p>
              These Terms continue while you use the Services. We may suspend or terminate access for nonpayment,
              security risks, or violations. You may terminate according to your order terms. Upon termination, your
              access ceases; we may retain or delete data per our Privacy Policy and any applicable law.
            </p>
          </section>

          <section>
            <h2>15. Changes to Terms</h2>
            <p>
              We may update these Terms from time to time. Material changes will be posted with an updated "Last
              Updated" date and, where required, communicated to you.
            </p>
          </section>

          <section>
            <h2>16. Governing Law and Dispute Resolution</h2>
            <p>
              These Terms are governed by the laws of the jurisdiction specified in your order (or, if none, the laws of
              the state/province where our principal place of business is located), excluding conflict-of-law principles.
              Disputes will be resolved in the courts or arbitration forum specified in your order; otherwise, in the
              courts of that jurisdiction.
            </p>
          </section>

          <section>
            <h2>17. General</h2>
            <p>
              If any provision is unenforceable, the remaining provisions remain in effect. These Terms and your order
              constitute the entire agreement regarding the Services. You may not assign these Terms without our consent;
              we may assign in connection with a merger, acquisition, or corporate reorganization. Notices may be
              provided via email or the Services interface.
            </p>
          </section>

          <section>
            <h2>Contact</h2>
            <p>
              For questions regarding these Terms, contact{' '}
              <a href="mailto:support@tablechat.io">support@tablechat.io</a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Terms;

